import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Upload, X, Video, ImageIcon, Car, MapPin, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import LocationPicker from "@/components/LocationPicker";

export default function PostIssue() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    location: "",
  });
  const [geo, setGeo] = useState({ lat: null, lng: null, radius: 25 });
  const [files, setFiles] = useState([]); // {path, content_type, name, size}
  const [uploads, setUploads] = useState([]); // {id, name, size, progress, status: 'uploading'|'failed', error?}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Prefill from profile
  useEffect(() => {
    if (user) {
      const loc = user.location || {};
      setGeo((g) => ({
        lat: loc.lat ?? g.lat,
        lng: loc.lng ?? g.lng,
        radius: user.radius_miles ?? g.radius ?? 25,
      }));
      if (loc.city || loc.zip) {
        setForm((f) => ({ ...f, location: f.location || [loc.city, loc.state, loc.zip].filter(Boolean).join(", ") }));
      }
    }
  }, [user]);

  const MAX_BYTES = 400 * 1024 * 1024; // 400MB — keep in sync with backend MAX_FILE_SIZE
  const MAX_LABEL = "400MB";
  const fmtMB = (b) => `${(b / (1024 * 1024)).toFixed(b > 100 * 1024 * 1024 ? 0 : 1)}MB`;

  const handleFiles = async (filesList) => {
    setUploading(true);
    for (const f of filesList) {
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} is ${fmtMB(f.size)} — max is ${MAX_LABEL}.`);
        continue;
      }
      const id = `${f.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setUploads((prev) => [...prev, { id, name: f.name, size: f.size, progress: 0, status: "uploading" }]);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 10 * 60 * 1000,
          onUploadProgress: (evt) => {
            const total = evt.total || f.size;
            const pct = total ? Math.min(100, Math.round((evt.loaded * 100) / total)) : 0;
            setUploads((prev) => prev.map((u) => u.id === id ? { ...u, progress: pct } : u));
          },
        });
        // Promote to files list, drop from uploads list.
        setFiles((prev) => [...prev, { path: res.data.path, content_type: res.data.content_type, name: f.name, size: f.size }]);
        setUploads((prev) => prev.filter((u) => u.id !== id));
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Upload failed";
        setUploads((prev) => prev.map((u) => u.id === id ? { ...u, status: "failed", error: msg } : u));
        toast.error(`${f.name}: ${msg}`);
      }
    }
    setUploading(false);
  };

  const dismissUpload = (id) => setUploads((prev) => prev.filter((u) => u.id !== id));

  const removeFile = (path) => setFiles((prev) => prev.filter((p) => p.path !== path));

  const submit = async () => {
    if (!form.title || !form.description) {
      toast.error("Title and description required");
      return;
    }
    if (!form.category) {
      toast.error("Pick a category");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        vehicle_year: form.vehicle_year ? parseInt(form.vehicle_year) : null,
        file_paths: files.map((f) => f.path),
        lat: geo.lat,
        lng: geo.lng,
        radius_miles: geo.lat != null ? parseInt(geo.radius) || 25 : null,
      };
      const res = await api.post("/issues", payload);
      toast.success("Issue posted");
      navigate(`/issue/${res.data.issue_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <main className="max-w-[1100px] mx-auto px-4 sm:px-8 lg:px-12 py-10">
        <div className="label-eyebrow mb-3">NEW ISSUE · ANONYMOUS</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-10">Describe the problem.</h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-6">
            <div className="border border-white/10 bg-[#121214] p-6 sm:p-8 space-y-5">
              <div>
                <label className="label-eyebrow block mb-2">TITLE *</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-base" placeholder="Grinding noise on left turns" data-testid="issue-title-input" />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">DESCRIPTION *</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} className="input-base" placeholder="When did it start? What does it feel/sound like?" data-testid="issue-description-input" />
              </div>

              <div>
                <label className="label-eyebrow block mb-2">CATEGORY *</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="input-base"
                  data-testid="issue-category-select"
                >
                  <option value="">Pick the part of the car...</option>
                  {["Engine","Transmission","Brakes","Electrical","Suspension","Body","Tires","HVAC","Exhaust","Other"].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="label-eyebrow block mb-2">MAKE</label>
                  <input value={form.vehicle_make} onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })} className="input-base" placeholder="Toyota" data-testid="issue-make-input" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">MODEL</label>
                  <input value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} className="input-base" placeholder="Camry" data-testid="issue-model-input" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">YEAR</label>
                  <input type="number" value={form.vehicle_year} onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })} className="input-base" placeholder="2018" data-testid="issue-year-input" />
                </div>
              </div>

              <div>
                <label className="label-eyebrow block mb-2">LOCATION (ZIP / CITY)</label>
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input-base" placeholder="94107" data-testid="issue-location-input" />
              </div>

              {/* Geo radius — only shops within this radius of you will see this job */}
              <div className="border border-white/10 bg-[#09090B] p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow flex items-center gap-2"><MapPin className="w-3 h-3 text-[#FF3B30]" /> SHOP VISIBILITY RADIUS</div>
                  <div className="font-heading text-2xl font-black text-[#FF3B30]">{geo.radius}<span className="text-xs text-zinc-500 ml-1">MI</span></div>
                </div>

                <LocationPicker
                  lat={geo.lat}
                  lng={geo.lng}
                  onChange={(lat, lng) => setGeo((g) => ({ ...g, lat, lng }))}
                  radiusMiles={geo.radius}
                  showRadius
                  height="280px"
                />

                <div>
                  <label className="label-eyebrow block mb-2">RADIUS — {geo.radius} MI</label>
                  <input
                    type="range"
                    min="5"
                    max="100"
                    step="5"
                    value={geo.radius}
                    onChange={(e) => setGeo({ ...geo, radius: parseInt(e.target.value) })}
                    className="w-full accent-[#FF3B30]"
                    data-testid="issue-radius-slider"
                  />
                </div>

                {geo.lat == null ? (
                  <div className="text-[11px] text-[#FF9500]">Pin your location on the map for the radius to take effect — otherwise every approved shop will see this job.</div>
                ) : (
                  <div className="text-[11px] text-zinc-500">Only shops inside the red circle will be able to see this job.</div>
                )}
              </div>
            </div>

            {/* File upload */}
            <div className="border border-dashed border-white/20 bg-[#121214] p-8">
              <div className="flex items-center justify-between mb-4">
                <div className="label-eyebrow">MEDIA · ORIGINAL QUALITY · UP TO 400MB EACH</div>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="upload-file-btn"
                >
                  <Upload className="w-4 h-4" /> Upload
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                  data-testid="file-input"
                />
              </div>
              {/* Active upload progress */}
              {uploads.length > 0 && (
                <div className="mb-3 space-y-2" data-testid="upload-progress-list">
                  {uploads.map((u) => {
                    const failed = u.status === "failed";
                    return (
                      <div key={u.id} className="border border-white/10 bg-[#09090B] p-3" data-testid={`upload-progress-${u.id}`}>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {failed ? (
                              <X className="w-4 h-4 text-[#FF3B30] shrink-0" />
                            ) : (
                              <Upload className={`w-4 h-4 shrink-0 ${u.progress >= 100 ? "text-[#34C759]" : "text-[#FF3B30] animate-pulse"}`} />
                            )}
                            <div className="min-w-0">
                              <div className="text-xs truncate font-medium">{u.name}</div>
                              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                                {fmtMB(u.size)}{!failed && ` · ${u.progress}%`}{u.progress >= 100 && !failed && " · finalizing..."}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {failed && (
                              <span className="text-[10px] uppercase tracking-widest text-[#FF3B30]">Failed</span>
                            )}
                            {failed && (
                              <button onClick={() => dismissUpload(u.id)} className="text-zinc-500 hover:text-white" data-testid={`dismiss-upload-${u.id}`}>
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="h-1 bg-white/5 overflow-hidden">
                          <div
                            className={`h-full transition-all duration-200 ${failed ? "bg-[#FF3B30]" : u.progress >= 100 ? "bg-[#34C759]" : "bg-[#FF3B30]"}`}
                            style={{ width: `${failed ? 100 : u.progress}%` }}
                          />
                        </div>
                        {failed && u.error && (
                          <div className="text-[10px] text-[#FF3B30] mt-1.5 truncate">{u.error}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {files.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <Upload className="w-10 h-10 mx-auto mb-3 text-zinc-700" strokeWidth={1.2} />
                  <div className="text-sm">Drag photos or videos of the problem area.</div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {files.map((f) => (
                    <div key={f.path} className="relative border border-white/10 bg-[#09090B] p-3 flex items-center gap-3">
                      {f.content_type?.startsWith("image/") ? <ImageIcon className="w-5 h-5 text-[#007AFF]" /> : <Video className="w-5 h-5 text-[#FF9500]" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs truncate">{f.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-600">{f.content_type?.split("/")[1]}</div>
                      </div>
                      <button onClick={() => removeFile(f.path)} className="text-zinc-500 hover:text-white" data-testid={`remove-file-${f.path}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="lg:col-span-5 space-y-6">
            <div className="border border-white/10 bg-[#121214] p-6">
              <div className="flex items-start gap-3 mb-4">
                <ShieldCheck className="w-6 h-6 text-[#34C759]" strokeWidth={1.5} />
                <div className="text-sm text-zinc-300">
                  <strong className="text-white">Anonymous by default.</strong> Shops see your photos, description, and vehicle — nothing else. Your name and contact stay private until you accept a quote.
                </div>
              </div>
              <ul className="text-xs text-zinc-500 space-y-2 mb-5 pl-9">
                <li>• High-resolution photos & 4K videos up to 400MB each</li>
                <li>• Only nearby shops can see this job</li>
                <li>• Shops reply with private itemized quotes</li>
              </ul>
            </div>

            <div className="border border-white/10 bg-[#121214] p-6">
              <div className="flex items-start gap-3 mb-4">
                <Car className="w-6 h-6 text-[#FF3B30]" strokeWidth={1.5} />
                <div className="text-sm text-zinc-400">
                  Ready when you are. Post once, get private quotes back fast.
                </div>
              </div>
              <button onClick={submit} disabled={submitting} className="btn-primary w-full" data-testid="submit-issue-btn">
                {submitting ? "..." : "Post Anonymously"}
              </button>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
